import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

enum Currencies {
    USD = 'USD',
    CAD = 'CAD',
}

interface TenantTheme {
    readonly tenantCompanyId: number;
    readonly defaultColor?: string | null;
    readonly primaryColor: string;
    readonly secondaryColor: string;
    readonly successColor: string;
    readonly warningColor: string;
    readonly dangerColor: string;
    readonly infoColor: string;
    readonly defaultHighlightColor: string;
    readonly defaultHighlightAccentColor: string;
    readonly showBrandLabel: boolean;
}

interface TenantSettings extends Record<string, unknown> {
    readonly tenantTheme: TenantTheme;
}

interface Tenant {
    readonly id: number;
    readonly companyName: string;
    readonly countryId: number;
    readonly defaultCurrencyCode: Currencies;
    readonly tenantSettings: TenantSettings;
}

interface Group {
    readonly id: number;
    readonly companyId: number;
    readonly groupName: string;
    readonly description: string;
    readonly code?: string;
    readonly expandDispatching: boolean;
    readonly expanededDispatchingCompanies: number[];
}

interface Role {
    readonly id: string;
    readonly name: string;
    readonly displayName: string;
}

interface User {
    readonly userId: string;
    readonly userName: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly email: string;
    readonly fullName: string;
    readonly roles: Role[];
    readonly groups: Group[];
    readonly appGroups: number[];
    readonly tenantId: number;
    readonly companyId: number;
    readonly companyTenantId: number;
    readonly dispatchCompanyId: number;
    readonly employeeId: number;
    readonly isImpersonating?: boolean;
    readonly isAdmin: boolean;
}

export interface CommonState {
    readonly isAuthenticated?: boolean | null;
    readonly pageTitle: string;
    readonly formIsOpen: boolean;
    readonly version: string;
    readonly user?: User | null;
    readonly tenant?: Tenant | null;
    readonly tenantSettings?: TenantSettings | null;
    readonly tenantTheme?: TenantTheme | null;
    readonly timesheetNotifications: number;
}

const initialState: CommonState = {
    isAuthenticated: null,
    pageTitle: '',
    formIsOpen: false,
    version: '',
    user: null,
    tenant: null,
    tenantSettings: null,
    tenantTheme: null,
    timesheetNotifications: 0,
};

export const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
            state.isAuthenticated = action.payload;
        },
        setPageTitle: (state, action: PayloadAction<string>) => {
            state.pageTitle = action.payload;
        },
        setFormIsOpen: (state, action: PayloadAction<boolean>) => {
            state.formIsOpen = action.payload;
        },
        setVersion: (state, action: PayloadAction<string>) => {
            state.version = action.payload;
        },
        setUser: (state, action: PayloadAction<User>) => {
            state.user = action.payload;
        },
        setTenant: (state, action: PayloadAction<Tenant>) => {
            state.tenant = action.payload;
        },
        setTenantSettings: (state, action: PayloadAction<TenantSettings>) => {
            state.tenantSettings = action.payload;
        },
        setTenantTheme: (state, action: PayloadAction<TenantTheme>) => {
            state.tenantTheme = action.payload;
        },
        setTimesheetNotifications: (state, action: PayloadAction<number>) => {
            state.timesheetNotifications = action.payload;
        },
    },
});

export const {
    setIsAuthenticated, setPageTitle, setFormIsOpen, setVersion,
    setUser, setTenant, setTenantSettings, setTenantTheme,
    setTimesheetNotifications,
} = commonSlice.actions;

export default commonSlice.reducer;